import { useCallback, useMemo, useState } from "react";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import Button, { ButtonProps } from "@scripts/react/components/Button";
import {
    AnnotationsForFileAndFrameId,
    useCurrentMedia,
    usePromptLabels,
    useSetTextPromptMode,
    useTextPromptMode
} from "@/store";
import { AnalyticsFlowEvent } from "@/analytics/types";

import React from "react";
import { ModalWithIcon, ModalWithIconProps } from "@scripts/react/components/RfModal/ModalWithIcon";
import { useModel } from "@/contexts/ModelContext";

const ToggleTextPromptModeModal: React.FC<
    Omit<ModalWithIconProps, "title"> & { onToggle: () => void }
> = ({ onToggle, ...props }) => {
    return (
        <ModalWithIcon
            {...props}
            type="info"
            size="md"
            icon="fad fa-pencil"
            title="Want to edit these boxes?"
            onSubmit={onToggle}
            submitButtonProps={{ type: "secondary" }}
            submitButtonText="Switch to Editing Mode"
            onCancel={props.onClose}
            cancelButtonProps={{
                type: "purbo-ghost",
                className: "!bg-tint hover:!bg-tint-hover flex-1"
            }}
            cancelButtonText="Adjust Sliders"
            className="text-center"
        >
            <p>For the best model results, use the sliders rather than editing manually.</p>
            <p>
                Switching to Editing Mode lets you draw and edit boxes only.{" "}
                <span className="font-semibold">New boxes will not have outlines</span> and you'll
                need to restart to prompt with text.
            </p>
        </ModalWithIcon>
    );
};

export const ToggleTextPromptModeBtn = ({
    ...props
}: {} & Omit<ButtonProps, "onClick" | "children">) => {
    const { track } = useAnalytics();
    const textPromptMode = useTextPromptMode();
    const setTextPromptMode = useSetTextPromptMode();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const { setAnnotationsForFileAndFrameId, annotationsForFileAndFrameId } = useModel();
    const promptLabels = usePromptLabels();
    const currentMedia = useCurrentMedia();

    const onToggleTextPromptMode = useCallback(() => {
        const newMode = !textPromptMode;
        track({
            event: AnalyticsFlowEvent.TOGGLE_TEXT_PROMPT_MODE,
            data: { textPromptMode: newMode }
        });
        setAnnotationsForFileAndFrameId((prev) => {
            const newState: AnnotationsForFileAndFrameId = {};
            for (const fileId in prev) {
                for (const frameId in prev[fileId]) {
                    const annotations = prev[fileId][frameId];
                    const filteredAnnotations = annotations.filter((anno) => {
                        const promptLabel = promptLabels.get(anno.label);
                        if (!promptLabel) return false;
                        return (
                            !anno.confidence || anno.confidence >= promptLabel.confidenceThreshold
                        );
                    });
                    newState[fileId] = { ...newState[fileId], [frameId]: filteredAnnotations };
                }
            }
            return newState;
        });
        setTextPromptMode(newMode);
    }, [setTextPromptMode, textPromptMode, track, setAnnotationsForFileAndFrameId, promptLabels]);

    const onClickToggleTextPromptMode = useCallback(() => {
        track({
            event: AnalyticsFlowEvent.TOGGLE_TEXT_PROMPT_MODE_MODAL_OPENED,
            data: { textPromptMode }
        });
        setModalIsOpen(true);
    }, [textPromptMode, track]);

    // Check if there are any annotations on the current file
    const hasAnnotationsOnCurrentFile = useMemo(() => {
        if (!currentMedia?.file.id) return false;

        const fileAnnotations = annotationsForFileAndFrameId[currentMedia.file.id];
        if (!fileAnnotations) return false;

        // Check if there are any annotations across all frames of this file
        return Object.values(fileAnnotations).some(
            (frameAnnotations) => frameAnnotations && frameAnnotations.length > 0
        );
    }, [annotationsForFileAndFrameId, currentMedia?.file.id]);

    if (!textPromptMode) {
        return null;
    }

    const buttonText = hasAnnotationsOnCurrentFile
        ? "Want to edit these boxes?"
        : "Want to draw boxes?";

    return (
        <div className="flex flex-col gap-4">
            <Button
                {...props}
                type="purbo-ghost"
                onClick={onClickToggleTextPromptMode}
                className="w-full"
            >
                {buttonText}
            </Button>
            <ToggleTextPromptModeModal
                isOpen={modalIsOpen}
                onClose={() => setModalIsOpen(false)}
                onToggle={onToggleTextPromptMode}
            />
        </div>
    );
};
